import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { usePostiAvvisi } from './postiAvvisi';
import { alertAPI } from '../services/api';
import { vaiA, haDestinazione, ETICHETTA } from '../utils/destinazione';
import { periodoAvvisi } from '../utils/periodoAvvisi';
import '../styles/Alert.css';
import '../styles/AlertConsole.css';

/* mostra la data ESATTAMENTE come salvata, senza conversione di fuso */
const fmtDT = (s) => {
    if (!s) return '';
    const m = String(s).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!m) return String(s);
    const [, y, mo, d, h, mi] = m;
    return `${d}/${mo}/${y}, ${h}:${mi}`;
};

/* formatta una Date locale del browser in "YYYY-MM-DDTHH:MM" (ora locale) */
const pad = (n) => String(n).padStart(2, '0');
const toLocalInput = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

const AlertNotifier = () => {
    const [due, setDue] = useState([]);
    const [snoozeId, setSnoozeId] = useState(null);   // alert in modalità "posticipa a..."
    const [snoozeVal, setSnoozeVal] = useState('');
    /* Con la pila dei ritardi sotto, il Promemoria resta chiuso sulla sua
       testata e si apre con un clic (Alex, 26/09/2026): aperto, con la pila,
       saliva fino ai pulsanti in cima alla pagina. Senza pila e' sempre
       aperto (Alert.css decide, con :has). */
    const [apertoQui, setApertoQui] = useState(false);
    const navigate = useNavigate();
    /* Nell'angolo in basso a destra, insieme alla pila dei ritardi (26
       settembre 2026): in alto copriva la barra - il nome e «Esci» - e su
       un telefono le due finestre si coprivano fra loro. */
    const { angolo, promemoria } = usePostiAvvisi();
    /* Dentro il Layout l'apertura sta li', perche' la campanella della
       barra in alto la possa chiedere (postiAvvisi.js); fuori, qui. */
    const aperto = promemoria ? promemoria.aperto : apertoQui;
    const setAperto = promemoria ? promemoria.apri : setApertoQui;
    /* Il numero della campanella e' questo elenco, e nient'altro. */
    const segnala = promemoria && promemoria.segnala;
    useEffect(() => { if (segnala) segnala(due.length); }, [segnala, due.length]);

    const check = useCallback(async () => {
        try {
            const r = await alertAPI.getDue();
            if (r.data.success) setDue(r.data.data.alert || []);
        } catch (e) { /* silenzioso */ }
    }, []);

    useEffect(() => {
        check();
        const t = setInterval(check, periodoAvvisi(60000));
        return () => clearInterval(t);
    }, [check]);

    if (!due.length || !angolo) return null;

    const rimuovi = (id) => setDue(prev => prev.filter(a => a.id !== id));

    // OK: segna notificato (non ricompare finché non lo riattivi)
    const ok = async (a) => {
        try { await alertAPI.markNotificato(a.id); } catch (e) {}
        rimuovi(a.id);
    };

    /* Il riquadro portava sempre alla scheda generica del paziente, anche
       quando l'avviso diceva ESATTAMENTE dove andare: `riferimento_tipo` e
       `riferimento_id` arrivano gia' da /alert/due, e restavano inusati
       qui mentre dashboard, promemoria e compiti li adoperavano da tempo.
       Chi riceveva «manca il consenso sulla visita del 3 marzo» atterrava
       sull'anagrafica e la visita doveva ritrovarsela.

       `vaiA` ripiega sulla scheda del paziente quando il riferimento non
       c'e' o e' di un tipo che questa versione non conosce: meno preciso,
       mai sbagliato. Per gli avvisi di servizio - un lotto, un resoconto
       del Sistema TS - il paziente non c'e' affatto, e prima il pulsante
       portava a `/pazienti/null`. */
    const vai = async (a) => {
        await ok(a);
        if (haDestinazione(a)) vaiA(navigate, a);
        else if (a.paziente_id) navigate(`/pazienti/${a.paziente_id}`);
    };

    /* Il pulsante dice dove porta: «Vai al paziente» su un avviso che apre
       il magazzino era una promessa sbagliata.
       «Apri» e non «Vai a» perche' le etichette portano gia' l'articolo -
       «il magazzino», «la visita» - e «Vai a il magazzino» non si legge.
       E' anche la formula che usano gia' Promemoria e Compiti. */
    const etichettaVai = (a) => (haDestinazione(a) && ETICHETTA[a.riferimento_tipo]
        ? `Apri ${ETICHETTA[a.riferimento_tipo]}`
        : 'Vai al paziente');

    // Contrassegna come eseguito
    const eseguito = async (a) => {
        try { await alertAPI.update(a.id, { stato: 'fatto', notificato: true }); } catch (e) {}
        rimuovi(a.id);
    };

    // Posticipa di 1 ora rispetto ad ADESSO
    const posticipa1h = async (a) => {
        const nuovo = toLocalInput(new Date(Date.now() + 60 * 60 * 1000));
        try { await alertAPI.update(a.id, { data_ora: nuovo, notificato: false }); } catch (e) {}
        rimuovi(a.id);
    };

    // Posticipa a data/ora scelta
    const apriSnooze = (a) => {
        setSnoozeId(a.id);
        setSnoozeVal(toLocalInput(new Date(Date.now() + 60 * 60 * 1000)));
    };
    const confermaSnooze = async (a) => {
        if (!snoozeVal) return;
        try { await alertAPI.update(a.id, { data_ora: snoozeVal, notificato: false }); } catch (e) {}
        setSnoozeId(null); setSnoozeVal('');
        rimuovi(a.id);
    };

    const chiudiTutti = async () => {
        await Promise.all(due.map(a => alertAPI.markNotificato(a.id).catch(() => {})));
        setDue([]);
    };

    return createPortal(
        <div className="al-notif-overlay">
            <div className={`al-notif${aperto ? ' al-notif--aperto' : ''}`}>
                <div className="al-notif-head">
                    <button type="button" className="al-notif-titolo-testa" onClick={() => setAperto((x) => !x)}
                            aria-expanded={aperto}>
                        Promemoria ({due.length})
                        <span className="al-notif-apri">{aperto ? 'Chiudi' : 'Apri'}</span>
                    </button>
                    <button className="al-notif-close" onClick={chiudiTutti} title="Chiudi tutti">✕</button>
                </div>
                <div className="al-notif-body">
                    {due.map(a => (
                        <div key={a.id} className="al-notif-item">
                            <div className="al-notif-dt">{fmtDT(a.data_ora)}</div>
                            <div className="al-notif-titolo">{a.titolo}</div>
                            {/* Solo se il promemoria riguarda un paziente: un
                                avviso di servizio (un lotto, il Sistema TS)
                                scriveva «Paziente:» e poi niente. E il nome si
                                legge da `nome` e `cognome`, che e' come lo manda
                                /alert/due: fino al 26 settembre 2026 qui si
                                leggeva `paziente_nome`, che non arriva mai, e la
                                riga era vuota anche per i pazienti. */}
                            {a.paziente_id && (a.cognome || a.nome) && (
                                <div className="al-notif-pz">Paziente: {a.cognome} {a.nome}</div>
                            )}
                            {/* Tre righe al massimo: un promemoria di servizio
                                con un elenco lungo occupava mezzo schermo. Il
                                testo intero resta al passaggio del mouse. */}
                            {a.descrizione && <div className="al-notif-desc" title={a.descrizione}>{a.descrizione}</div>}

                            {snoozeId === a.id ? (
                                <div className="al-notif-snooze">
                                    <input type="datetime-local" value={snoozeVal}
                                           onChange={e => setSnoozeVal(e.target.value)} />
                                    <div className="al-notif-snooze-actions">
                                        <button className="al-notif-go" onClick={() => confermaSnooze(a)}>Conferma</button>
                                        <button className="al-notif-ok" onClick={() => { setSnoozeId(null); setSnoozeVal(''); }}>Annulla</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="al-notif-actions">
                                    {(haDestinazione(a) || a.paziente_id) && (
                                        <button className="al-notif-go" onClick={() => vai(a)}>
                                            {etichettaVai(a)}
                                        </button>
                                    )}
                                    <button className="al-notif-done" onClick={() => eseguito(a)}>Eseguito</button>
                                    <button className="al-notif-snz" onClick={() => posticipa1h(a)}>+1h</button>
                                    <button className="al-notif-snz" onClick={() => apriSnooze(a)}>Posticipa a…</button>
                                    <button className="al-notif-ok" onClick={() => ok(a)}>OK</button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>,
        angolo
    );
};

export default AlertNotifier;
